import { computed, type MaybeRefOrGetter, toValue } from 'vue'
import { SELF_PERSPECTIVE_KEY } from '@/core/models/perspective'

type FeedbackGoalProgressColor = 'primary' | 'secondary' | 'tertiary' | 'neutral'

export type FeedbackGoalProgressEntry = {
  readonly perspectiveKey: string
  readonly count: number
  readonly target: number
}

export type FeedbackGoalProgress = ReadonlyArray<FeedbackGoalProgressEntry>

export interface CoacheeFeedbackGoalData {
  id: string
  feedbackTopic: { id: string; title: string; icon?: string }
  progress: FeedbackGoalProgress
}

export interface CoacheeFeedbackGoalPerspectiveProgress {
  perspectiveKey: string
  percentage: number
  done: number
  goal: number
  color: FeedbackGoalProgressColor
}

const NON_SELF_COLORS: FeedbackGoalProgressColor[] = ['tertiary', 'neutral', 'secondary']

export const useFeedbackGoalProgress = (feedbackGoal: MaybeRefOrGetter<FeedbackGoalProgress>) => {
  const perspectives = computed<CoacheeFeedbackGoalPerspectiveProgress[]>(() => {
    const currentGoal = toValue(feedbackGoal)
    const result: CoacheeFeedbackGoalPerspectiveProgress[] = []
    let nonSelfIndex = 0

    for (const entry of currentGoal) {
      if (entry.target <= 0) {
        continue
      }

      const isSelf = entry.perspectiveKey === SELF_PERSPECTIVE_KEY
      const color: FeedbackGoalProgressColor = isSelf
        ? 'primary'
        : NON_SELF_COLORS[nonSelfIndex++ % NON_SELF_COLORS.length]

      result.push({
        perspectiveKey: entry.perspectiveKey,
        percentage: (entry.count / entry.target) * 100,
        done: entry.count,
        goal: entry.target,
        color,
      })
    }

    return result
  })

  const totalDone = computed(() =>
    perspectives.value.reduce((total, perspective) => total + perspective.done, 0),
  )

  const totalGoal = computed(() =>
    perspectives.value.reduce((total, perspective) => total + perspective.goal, 0),
  )

  const totalPercentage = computed(() => {
    if (totalGoal.value === 0) {
      return 0
    }

    return (totalDone.value / totalGoal.value) * 100
  })

  return {
    totalDone,
    totalGoal,
    totalPercentage,
    perspectives,
  }
}
